/* pet-passport-uk.js — vanilla progressive enhancement for the UK Pet Passport / Travel page.
 * Replaces the framework runtime from the React prototype (TravelContent.jsx):
 *   1. the "Travelling to specific countries" tabs — CountrySelector
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the (France) country panel is still visible.
 *
 * The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
 * Answers are rendered into the static body.html (for SEO/crawlability), so this
 * file only owns the CountrySelector tab widget.
 */
(function () {
  'use strict';

  // ---- Tiny SVG icon builder (ported from illustrations.jsx Icon) -----------
  var ICON_PATHS = {
    check: '<path d="M20 6 9 17l-5-5"></path>',
    shield: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"></path><path d="m9 12 2 2 4-4"></path>',
    book: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z"></path>',
    alert: '<path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"></path><path d="M12 9v4M12 17h.01"></path>',
    arrowUpRt: '<path d="M7 17 17 7M8 7h9v9"></path>',
  };
  function icon(name, size, color, style) {
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"' +
      (style ? ' style="' + style + '"' : '') + ' aria-hidden="true">' + ICON_PATHS[name] + '</svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- Country selector (tabs) ---------------------------------------------
  // Data ported verbatim from TV_COUNTRIES in TravelContent.jsx.
  var TV_COUNTRIES = [
    { id: 'france', label: 'France', doc: 'Animal Health Certificate (AHC)',
      points: ['Standard EU rules - microchip + in-date rabies vaccination', 'AHC issued by an Official Veterinarian within 10 days of travel', 'Tapeworm treatment is needed for your return to GB, not for entry to France'],
      note: 'The most popular route. Check approved travel routes and your ferry/Eurotunnel pet policy.' },
    { id: 'ireland', label: 'Ireland (ROI)', doc: 'Animal Health Certificate (AHC)',
      points: ['Republic of Ireland follows EU rules - an AHC is required', 'Microchip + in-date rabies vaccination', 'Dogs need tapeworm treatment 24–120 hours before arrival (Ireland is a tapeworm-rule country)'],
      note: 'Don’t confuse ROI with Northern Ireland - see the NI option, which is different.' },
    { id: 'ni', label: 'Northern Ireland', doc: 'NI Pet Travel Document (free)',
      points: ['For GB→NI travel (not onward to ROI/EU), you can use the free NI Pet Travel Document', 'Microchip required; no rabies vaccination or tapeworm treatment, and no vet signature needed', 'It lasts the lifetime of the pet - or you can still use an AHC if you prefer'],
      note: 'Apply free via GOV.UK. If you travel onward from NI into the EU, you need an AHC.' },
    { id: 'spain', label: 'Spain', doc: 'Animal Health Certificate (AHC)',
      points: ['Standard EU rules - microchip + in-date rabies vaccination', 'AHC issued within 10 days of travel by an Official Veterinarian'],
      note: 'As with all EU travel, plan the rabies timing weeks ahead.' },
    { id: 'eu', label: 'Wider EU', doc: 'Animal Health Certificate (AHC)',
      points: ['One AHC covers entry to your first EU country, then onward travel within the EU and your return to GB', 'Microchip + in-date rabies vaccination throughout', 'Some countries (e.g. Finland, Malta) have extra tapeworm rules'],
      note: 'Always confirm the destination-specific rules on GOV.UK before booking.' },
    { id: 'outside', label: 'Outside the EU', doc: 'Varies - check GOV.UK', external: true,
      points: ['Non-EU destinations (e.g. USA) have their own rules and an AHC is not the right document', 'The Crown Dependencies (Isle of Man, Channel Islands) need no pet documents - treated like travel within GB', 'Some destinations need export health certificates and blood tests'],
      note: 'Rules vary widely - check the requirements for your specific destination on GOV.UK well ahead.' },
  ];

  // All country panels are now rendered into the static HTML (data-sn-country) by
  // the converter — crawlable. This just toggles which one is visible. No innerHTML
  // rebuild, so the full content of every country stays in the DOM for crawlers.
  function initCountrySelector(scope) {
    var tabs = scope.querySelectorAll('button[style*="border-radius:999px"][style*="font-size:14.5px"]');
    var panels = scope.querySelectorAll('[data-sn-country]');
    if (!tabs.length || !panels.length) return;

    var idByLabel = {};
    TV_COUNTRIES.forEach(function (c) { idByLabel[c.label] = c.id; });

    function select(id, label) {
      tabs.forEach(function (btn) {
        var on = btn.textContent.trim() === label;
        btn.style.border = on ? '2px solid var(--sn-forest)' : '1.5px solid var(--sn-line)';
        btn.style.background = on ? 'var(--sn-forest)' : '#fff';
        btn.style.color = on ? 'var(--sn-cream)' : 'var(--sn-ink)';
      });
      panels.forEach(function (p) {
        p.style.display = p.getAttribute('data-sn-country') === id ? '' : 'none';
      });
    }

    tabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var label = btn.textContent.trim();
        if (idByLabel[label]) select(idByLabel[label], label);
      });
    });
  }

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-pet-passport-uk');
    if (!page) return;
    initCountrySelector(page);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
